import type { FragmentSummary } from "@/api/affdo";
import { formatRmsdKcal, useFragmentDihedrals, useRunFragments } from "@/api/affdo";
import { FragmentSummaryTable } from "@/components/affdo/FragmentSummaryTable";
import { EmptyState } from "@/components/layout/EmptyState";
import { resolveFragmentId } from "@/components/runs/fragment-selection";
import type { TorsionPick } from "@/components/runs/torsion-selection";
import { orderedTorsionIds, resolveTorsionId } from "@/components/runs/torsion-selection";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * The run's torsion fits, as the workspace's side panel (flowstack#991).
 *
 * This is the "plot info" half of #991's pairing, and it serves both visual
 * modes:
 *
 * - beside the **plots** (`showList={false}`) it is the numbers for the
 *   fragment whose curves are on screen — so the panel is about THAT plot
 *   rather than about the run in general;
 * - beside the **molecule** (`showList`) it is the whole fragment list, which
 *   is the same pairing inverted: the structure has the main area, and the
 *   fits are the reference beside it.
 *
 * The list is `FragmentSummaryTable` — the Analysis tab's component, not a
 * second one. Every number here is a field the `TorsionRefinementResult`
 * projection actually carries (`n_fitted` / `n_failed` / `n_total` and the
 * RMSD pair); nothing is derived into a claim the artifact does not make, and
 * a run with no fits says *not published yet* rather than showing an empty
 * table.
 */
export interface RunFragmentFitsProps {
  runId: string;
  /** The DAG poll's progress fingerprint, so fits appear as they land. */
  progressKey: string;
  /**
   * Whether the flow could still publish more fits (Rupert PR-999 r1).
   *
   * Decides *pending* vs *absent* in the empty state and nothing else — the
   * same split `run-visual-views.ts` draws, for the same reason. Without it
   * this panel told a FINISHED run that its results "appear here as each
   * torsion_fit node completes", which is a promise about a run that can no
   * longer produce anything: the flowstack#923 conflation, inverted.
   */
  runIsLive: boolean;
  /** The workspace's fragment selection — shared with the plots. */
  selectedFragmentId: string | null;
  onFragmentSelect: (fragmentId: string) => void;
  /**
   * The workspace's RAW torsion pick, shared with the plots the same way
   * (VIZ U5.1) — IGNORED under `showList`, which is the molecule pairing,
   * where no single torsion is on screen to name (`namesTorsion` below). The
   * caller may pass it unconditionally; this component decides.
   *
   * Resolved HERE rather than handed down already-resolved: this panel asks the
   * same dihedrals query the plots do (identical key, so TanStack serves one
   * request for both) and runs the same `resolveTorsionId` over the same
   * ordering, which is what guarantees the caption and the curves agree. The
   * alternative — the plots reporting their resolved id back up — makes the
   * selection two-way and the render order load-bearing.
   */
  selectedTorsion?: TorsionPick | null;
  /** Show the selectable fragment list, not just the current fragment. */
  showList: boolean;
}

export function RunFragmentFits({
  runId,
  progressKey,
  runIsLive,
  selectedFragmentId,
  onFragmentSelect,
  selectedTorsion = null,
  showList,
}: RunFragmentFitsProps): JSX.Element {
  const fragments = useRunFragments(runId, { refreshKey: progressKey });

  // Resolved through the shared helper, so this panel and the plots in the
  // main area can never caption one fragment beside another's curves.
  const rows = fragments.data?.fragments ?? [];
  const fragmentId = resolveFragmentId(rows, selectedFragmentId);

  // Only when a torsion is what the main area is showing. `showList` is the
  // molecule pairing, where the whole fragment list is the subject and there is
  // no one torsion to name — so no query is made there.
  const namesTorsion = !showList;
  const dihedrals = useFragmentDihedrals(runId, fragmentId ?? "", {
    enabled: namesTorsion && fragmentId !== null,
    refreshKey: progressKey,
  });
  const torsionId = namesTorsion
    ? resolveTorsionId(
        orderedTorsionIds(dihedrals.data?.fitted, dihedrals.data?.failed),
        selectedTorsion,
        fragmentId
      )
    : null;

  if (fragments.isLoading) return <Skeleton className="h-40 w-full" />;

  if (fragments.isError) {
    // "We could not read this" is a different claim from "there is nothing
    // yet", and the panel must never let the first masquerade as the second
    // (flowstack#923).
    return (
      <EmptyState
        title="Could not load torsion results."
        description={fragments.error instanceof Error ? fragments.error.message : "Unknown error."}
      />
    );
  }

  if (rows.length === 0) {
    // "Not yet" and "never" are different facts about someone's run, and only
    // one of them is worth waiting on.
    return runIsLive ? (
      <EmptyState
        title="No torsion fits published yet."
        description="Fragment results appear here as each torsion_fit node completes."
      />
    ) : (
      <EmptyState
        title="This run produced no torsion fits."
        description="Not every flow refits torsions — and a run can finish without any torsion_fit node publishing a result."
      />
    );
  }

  const current = rows.find((f) => f.fragment_id === fragmentId) ?? null;

  return (
    <div className="flex flex-col gap-3">
      {showList && (
        // `compact`: the panel is 26rem, and the table's five numeric columns
        // clip at that width — which is the same too-narrow-numeric-layout
        // failure as the overlapping `fit_rmsd_above_thre` labels the operator
        // reported. Here the list only has to SELECT; the selected row's RMSDs
        // are spelled out below, where there is room for them.
        <FragmentSummaryTable
          fragments={rows}
          selectedFragmentId={fragmentId}
          onSelect={onFragmentSelect}
          compact
        />
      )}
      {current !== null && <FragmentFitNumbers fragment={current} torsionId={torsionId} />}
    </div>
  );
}

/**
 * The selected fragment's own numbers — the info for the plot on screen.
 *
 * Three surfaces, in the order the mock puts them (Tier-2 U1, redesign
 * `plans/assets/flowstack-redesign-mock.html`):
 *
 * 1. **tiles** for the two RMSD figures, because they are the numbers someone
 *    came here to read and a label/value text row buries them at the same
 *    weight as the ids beside them;
 * 2. **identifier rows**, monospace, because `frag-0` / `tor-0` / a status
 *    literal are strings to compare character by character, not prose;
 * 3. **a proportion bar** for the fitted/failed split, which was previously
 *    stated only in a sentence — a shape is what makes "one of these three
 *    failed" legible without reading.
 *
 * **Exported since VIS/V6**, so the Analysis tab's rail shows the SAME figures
 * beside its curves rather than a second set drawn its own way. It takes a
 * `FragmentSummary`, and the report's `FragmentAnalysis` is a superset of it
 * by construction (`api/analysis-report.ts` states that the per-fragment field
 * shapes are identical on purpose), so the report can hand one over unchanged
 * — no adapter, and no second definition of "max RMSD" to drift.
 *
 * Pure presentation: every number is a field on the fragment it is given, so
 * it makes no request of its own and works from a materialised report exactly
 * as it does from the live projection.
 */
export function FragmentFitNumbers({
  fragment,
  torsionId,
}: {
  fragment: FragmentSummary;
  torsionId: string | null;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {/* SEPARATE BORDERED CARDS, not a divided strip — direction B's one
          structural change to this panel (VIS/V0b), and the only one in the
          repaint that a token could not deliver.

          This used to be `grid-cols-2 gap-px … border bg-border-subtle` with
          each tile on `bg-surface-1`: one clipped box whose 1px gap showed the
          hairline colour through, so the two figures read as two cells of one
          object. They are not one object. They are two independent
          measurements of the same fragment, and B draws each as its own card
          so nothing about the arrangement implies the second is a continuation
          of the first.

          `gap-2.5` (10px) is the artboard's gap
          (`dev/design/flowstack-workspace/Main.dc.html`); the corner and the
          hairline come from `<Card>` rather than from a class string here, so
          the tiles follow `--radius` with every other panel-scale surface. */}
      <div className="grid grid-cols-2 gap-2.5">
        <StatTile
          label="Max RMSD"
          value={fragment.max_fit_rmsd_kcal_per_mol}
          testId="stat-max-rmsd"
        />
        <StatTile
          label="Mean RMSD"
          value={fragment.mean_fit_rmsd_kcal_per_mol}
          testId="stat-mean-rmsd"
        />
      </div>
      <FragmentFitRows fragment={fragment} torsionId={torsionId} />
      <TorsionProportion fragment={fragment} />
    </div>
  );
}

function FragmentFitRows({
  fragment,
  torsionId,
}: {
  fragment: FragmentSummary;
  torsionId: string | null;
}): JSX.Element {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
      <Row label="Fragment">
        <span className="font-mono" data-testid="side-panel-fragment">
          {fragment.fragment_id}
        </span>
      </Row>
      {/* The torsion the plots have SELECTED — present in both of the plots'
          views, absent beside the molecule.

          It is deliberately NOT conditioned on focus-vs-overview, and an
          earlier version of this comment claimed otherwise (Rupert PR-1077 r1):
          the view toggle is `RunTorsionPlots`' own presentation state and never
          reaches this panel, so a claim about it here would be one the code
          cannot honour. It also should not: in the overview the grid MARKS that
          same torsion (`ring` + `aria-current`), so the row names something
          visible rather than captioning an unshown torsion. Beside the molecule
          there is no torsion on screen at all, which is why `showList` — and
          only `showList` — drops the row. */}
      {torsionId !== null && (
        <Row label="Torsion">
          <span className="font-mono" data-testid="side-panel-torsion">
            {torsionId}
          </span>
        </Row>
      )}
      {/* A closed-vocabulary literal (`ok` / `partial` / …), not prose: it is
          compared against the same string in the API and the tables, so it is
          set like an identifier. */}
      <Row label="Status">
        <span className="font-mono">{fragment.status}</span>
      </Row>
    </dl>
  );
}

/**
 * One RMSD figure as a tile: small uppercase key, the number at display size in
 * tabular figures, the unit as a distinct lighter suffix.
 *
 * `tabular-nums` is load-bearing rather than typographic taste — two tiles
 * side by side are read as a pair, and proportional digits make `0.297` and
 * `0.211` different widths, so the decimal points do not line up. The figure
 * itself is printed at the app's ONE RMSD precision (`RMSD_DECIMALS`) for the
 * same reason at a larger scale: this tile and the failed panel's status line
 * are the same metric, and a `1.900` here beside a `1.90` there is a difference
 * the reader has to rule out.
 *
 * **A missing RMSD is "not reported", never 0 and never a dash.** `null` is
 * what an all-failed fragment carries, and a number there would assert a
 * perfect fit for a fragment that has none. It renders at text size rather
 * than display size because it is a sentence, not a figure.
 */
function StatTile({
  label,
  value,
  testId,
}: {
  label: string;
  value: number | null;
  testId: string;
}): JSX.Element {
  return (
    // A `<Card>` rather than a bare div: after V0b these tiles ARE cards, and
    // taking the recipe from the primitive is what keeps their corner and
    // hairline on `--radius`/`--border-subtle` instead of on a second copy of
    // the string. `p-card` for the inset, so they step with `[data-density]`
    // like every other content card — the tile was on a literal `px-3 py-2`
    // and was therefore invisible to compact mode.
    <Card className="p-card" data-testid={testId}>
      {/* `.label-micro` is the app's ONE micro-label treatment (`globals.css`).
          It was spelled `text-[10px] uppercase tracking-wider` here — the same
          intent, at a size the type scale cannot see and a tracking that does
          not move with it. */}
      <div className="label-micro text-muted-foreground">{label}</div>
      {value === null ? (
        <div className="mt-1 text-xs text-muted-foreground">not reported</div>
      ) : (
        <div className="mt-0.5 text-lg font-semibold tabular-nums leading-tight tracking-tight text-foreground">
          {formatRmsdKcal(value)}
          <span className="ml-1 text-[11px] font-normal text-muted-foreground">kcal/mol</span>
        </div>
      )}
    </Card>
  );
}

/**
 * The fitted/failed split as a proportion, with the counts beside it.
 *
 * **Status tokens, not the chart series** — these two bands mean *succeeded*
 * and *failed for a scientific reason*, which is exactly what
 * `--state-completed` and `--state-failed-scientific` name everywhere else in
 * the app; the series tokens are for curves and carry no verdict.
 *
 * Every band is also spelled out in the counts under it. That is the
 * requirement for colour-blind and low-contrast readers (the aqua step
 * measures 2.82:1 on white, below the 3:1 bar, so it may not carry meaning
 * alone) — and it is why the bar can stay 6 px tall.
 *
 * `n_total` is the denominator, NOT `n_fitted + n_failed`: the projection
 * carries its own total and a torsion can be neither yet (still running, or
 * skipped). Those torsions are the unfilled remainder, and they are counted in
 * words rather than left as a gap nobody can name.
 */
function TorsionProportion({ fragment }: { fragment: FragmentSummary }): JSX.Element {
  const { n_fitted: fitted, n_failed: failed, n_total: total } = fragment;
  // ONE total, read by both the bar and the label (Rupert PR-1092 Summary).
  //
  // The guard against a payload whose parts exceed its own total is right — the
  // bands are widths, and a >100% row would overflow the rail rather than say
  // anything — but it used to apply to the widths ALONE, so a malformed
  // 2-fitted/2-failed-of-3 payload drew two bands filling the rail against 4
  // while the label beside them still read "of 3", a number smaller than the
  // counts it was the total for. The counts are the authoritative channel here
  // (they are what a reader who cannot use colour has), so the total is taken
  // from them where the payload contradicts itself, and the picture and the
  // text can no longer disagree.
  const reportedTotal = Math.max(total, fitted + failed);
  // ...and only the width divisor needs the further floor of 1: an empty
  // fragment is legitimately "of 0", but 0/0 is not a width.
  const denominator = Math.max(reportedTotal, 1);
  const pending = Math.max(0, total - fitted - failed);
  const pct = (n: number): string => `${(n / denominator) * 100}%`;

  return (
    <div>
      {/* Decorative, and marked so: every band it draws is spelled out in the
          counts underneath, so announcing the bar as well would read the split
          twice. */}
      <div
        className="flex h-1.5 w-full overflow-hidden rounded-full bg-surface-3"
        aria-hidden
        data-testid="torsion-proportion-bar"
      >
        <div className="h-full bg-state-completed" style={{ width: pct(fitted) }} />
        <div className="h-full bg-state-failed-scientific" style={{ width: pct(failed) }} />
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <Count className="bg-state-completed" n={fitted} label="fitted" />
        <Count className="bg-state-failed-scientific" n={failed} label="failed" />
        {pending > 0 && <Count className="bg-surface-3" n={pending} label="not fitted yet" />}
        <span className="ml-auto tabular-nums" data-testid="torsion-proportion-total">
          of {reportedTotal}
        </span>
      </div>
      {failed > 0 && (
        // The honesty line the redesign is not allowed to trade away
        // (flowstack#923): a failed torsion keeps its standard parameters, and
        // that is a recorded outcome rather than a hole in the data. Stated
        // WITHOUT naming GAFF2 — this projection does not carry the base force
        // field, and the panels that do name it read it from the artifact.
        <p
          className="mt-2 text-[11px] leading-snug text-muted-foreground"
          data-testid="retention-note"
        >
          A failed torsion keeps its standard force-field parameters — a recorded outcome, not
          missing data.
        </p>
      )}
    </div>
  );
}

function Count({
  className,
  n,
  label,
}: {
  className: string;
  n: number;
  label: string;
}): JSX.Element {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn("h-1.5 w-1.5 rounded-[2px]", className)} />
      <span className="tabular-nums text-foreground">{n}</span> {label}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <>
      <dt className="uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="tabular-nums text-foreground">{children}</dd>
    </>
  );
}
